import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { GET, POST } from '../service/base-api-service';
import { getIsAdminApi } from './auth.store';
import { monthToNumbers } from '../utils/utils';
import { serviceFormTypesObj } from '../utils/constant';

const initialState = {
  isLoading: false,
  isLoadingError: false,
  detailedReports: null,
  selectedCompany: '',
  selected: {
    serviced:{
      tenant: '',
      month: '',
      year: new Date().getFullYear().toString(),
      reports: [],
    },
    'after-service':{
      tenant: '',
      site: '',
      month: '',
      year: new Date().getFullYear().toString(),
      reports: [],
    },
    handover: {
      tenant: '',
      site: '',
      month: '',
      year: new Date().getFullYear().toString(),
      reports: [],
    }
  }
};

const parseDateFromFileName = (fileName = '') => {
  const dateFormats = [
    /(\d{4})[-_.](\d{2})[-_.](\d{2})/,
    /(\d{2})[-_.](\d{2})[-_.](\d{4})/,
  ];

  for (const regex of dateFormats) {
    const match = String(fileName).match(regex);
    if (!match) continue;

    if (match[0].match(/^\d{4}/)) {
      const [year, month, day] = [match[1], match[2], match[3]];
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) return parsed;
      continue;
    }

    const [day, month, year] = [match[1], match[2], match[3]];
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

const getHandoverReportDate = (report = {}) => {
  const candidates = [
    report.servicedDate,
    report.uploadedAt,
    report.createdAt,
    report.createdDate,
    report.lastModified,
    report.serviceDate,
    report.date,
  ].filter(Boolean);

  for (const value of candidates) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return parseDateFromFileName(report.fileName || report.name || '');
};

export const fetchReports = createAsyncThunk(
  'fetchReports',
  async ({ month, type, company, tenantId, siteId }, { getState }) => {
  if (type === 'handover') {
    const resolvedTenantId = tenantId || company;
    const resolvedSiteId = siteId || getSite(getState())(type);

    if (!resolvedTenantId) {
      return [];
    }

    const adminPrefix = getIsAdminApi(getState()) ? 'admin/' : '';
    const year = Number(getYear(getState())(type));
    const { from, to } = monthToNumbers(month);
    const fromMonth = Number(from);
    const toMonth = Number(to);

    const list = await GET(`v1/${adminPrefix}service-handover`, {
      params: {
        tenantId: resolvedTenantId,
        ...(resolvedSiteId ? { siteId: resolvedSiteId } : {}),
      },
    });

    return (Array.isArray(list) ? list : []).filter((item) => {
      const parsedDate = getHandoverReportDate(item);
      if (!parsedDate) return true;

      return parsedDate.getFullYear() === year && parsedDate.getMonth() + 1 >= fromMonth && parsedDate.getMonth() + 1 <= toMonth;
    });
  }

  const { from, to } = monthToNumbers(month);
  const resolvedTenantId = company || tenantId || getTenant(getState())(type);
  const isAdminApi = getIsAdminApi(getState());
  const year = getYear(getState())(type);
  const toDate = to === '04' ? '30' : '31';
  const serviceType = type === 'serviced' ? 'service' : 'afterService';

  const prefixes = Array.from(new Set([isAdminApi ? 'admin/' : '', isAdminApi ? '' : 'admin/']));

  const fetchByTypeWithFallback = async (serviceFormType) => {
    let lastError = null;

    for (const prefix of prefixes) {
      const baseUrl = `v1/${prefix}report/${serviceType}/meta/01.${from}.${year}-00:00:00/${toDate}.${to}.${year}-23:59:59`;
      const query = resolvedTenantId ? `?tenantId=${resolvedTenantId}&type=${serviceFormType}` : `?type=${serviceFormType}`;

      try {
        return await GET(`${baseUrl}${query}`);
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError || { status: 500, message: 'Unable to load reports' };
  };

  const [d1, d2, d3] = await Promise.all([
    fetchByTypeWithFallback(serviceFormTypesObj.SERVICE.name),
    fetchByTypeWithFallback(serviceFormTypesObj.AUDIT.name),
    fetchByTypeWithFallback(serviceFormTypesObj.COMMISSION.name),
  ]);

  return [...d1, ...d2, ...d3];
});

export const getDetailedReport = createAsyncThunk('detailedReport', async ({ id, tenantId }, { getState }) => {
  // let url = `v1/report/serviceForm/${id}`;
  // if (getIsAdminApi(getState())) url = `v1/admin/report/serviceForm/${id}?tenantId=${tenantId}`;

  let url = `v1/admin/report/serviceForm/${id}?tenantId=${tenantId}`;
  


  return GET(url);
});

export const updateQuestion = createAsyncThunk('updateQuestion', async ({id, tenantId, question, fixed}, { dispatch, getState }) => {
  const {serviceFormId} = detailedReports(getState());
  let url = `v1/report/serviceForm/update-question/${serviceFormId}`;
  if (getIsAdminApi(getState())) url = `v1/admin/report/serviceForm/update-question/${serviceFormId}?tenantId=${tenantId}`;
  await POST(url, {
    question,
    fixed: !fixed
  });
  return dispatch(getDetailedReport({ id, tenantId }))
});

export const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setYear: (state, { payload }) => {
      state.selected[payload.type].year = payload.value
    },
    setTenant: (state, { payload }) => {
      state.selected[payload.type].tenant = payload.value
    },
    setSite: (state, { payload }) => {
      state.selected[payload.type].site = payload.value
    },
    setMonth: (state, { payload }) => {
      state.selected[payload.type].month = payload.value
    },
    initiate: (state, { payload }) => {
      const keys = Object.keys(state.selected);
      keys.forEach(key=>{
        state.selected[key].tenant = payload
        if (key === 'handover') {
          state.selected[key].site = '';
        }
      })
    }
  },
  extraReducers: {
    [fetchReports.pending]: (state, { meta: { arg } }) => {
      state.month = arg.month;
      state.isLoading = true;
    },
    [fetchReports.rejected]: (state) => {
      state.isLoading = false;
      state.reports = [];
    },
    [fetchReports.fulfilled]: (state, { meta: { arg }, payload }) => {
      state.selected[arg.type].reports = payload;
      state.isLoading = false;
    },

    [getDetailedReport.pending]: (state) => {
      state.detailedReports = null;
      state.isLoading = true;
    },
    [getDetailedReport.rejected]: (state) => {
      state.detailedReports = null;
      state.isLoading = false;
    },
    [getDetailedReport.fulfilled]: (state, { payload }) => {
      state.detailedReports = payload;
      state.isLoading = false;
    },
  },
});

export const { setSelection, setType, setYear, setMonth, setTenant, setSite, initiate } = reportsSlice.actions;

const getReportsStore = (state) => state.reports;

export const getYear = createSelector(getReportsStore, (store) => (type) => store.selected[type].year);
export const getMonth = createSelector(getReportsStore, (store) => (type) => store.selected[type].month);
export const getTenant = createSelector(getReportsStore, (store) => (type) => store.selected[type].tenant);
export const getSite = createSelector(getReportsStore, (store) => (type) => store.selected[type].site);
export const getReports = createSelector(getReportsStore, (store) => (type) => store.selected[type].reports);
export const getReportsLoading = createSelector(getReportsStore, (store) => store.isLoading);
export const detailedReports = createSelector(getReportsStore, (store) => store.detailedReports);

export default reportsSlice.reducer;
